import { extendTheme } from "@chakra-ui/react";
import { Button } from "./button";
import { Tabs } from "./tabs";
import { Switch } from "./switch";
import { Table } from "./table";
import { Modal } from "./modal";
import { Popover } from "./popover";

export const ALL_COLOR_SCHEMES = [
  "whiteAlpha",
  "blackAlpha",
  "gray",
  "brand",
  "red",
  "orange",
  "yellow",
  "hazel",
  "green",
  "teal",
  "blue",
  "cyan",
  "purple",
  "pink",
];

export const COLORFUL_COLOR_SCHEMES = ALL_COLOR_SCHEMES.filter(
  (colorScheme) =>
    !["whiteAlpha", "blackAlpha", "gray", "brand"].includes(colorScheme),
);

export const loopThroughColors = (index: number, colors: string[]) =>
  colors[index % colors.length];

/**
 * https://refine.dev/docs/ui-integrations/chakra-ui/theming/#theme-customization
 */
export const theme = extendTheme({
  config: {
    initialColorMode: "light",
    useSystemColorMode: false,
  },
  fonts: {
    heading: "var(--font-manrope)",
    body: "var(--font-manrope)",
  },
  components: {
    Button,
    Tabs,
    Switch,
    Table,
    Modal,
    Popover,
  },
  // https://www.figma.com/file/7Qggsl0OaLgYarFsLPcNzP/Bookla-Platform-%5BIn-Progress%5D?node-id=2611%3A143079&mode=dev
  colors: {
    black: "#282828",
    white: "#FFF",
    whiteAlpha: {
      50: "rgba(255, 255, 255, 0.04)",
      100: "rgba(255, 255, 255, 0.06)",
      200: "rgba(255, 255, 255, 0.08)",
      300: "rgba(255, 255, 255, 0.16)",
      400: "rgba(255, 255, 255, 0.24)",
      500: "rgba(255, 255, 255, 0.36)",
      600: "rgba(255, 255, 255, 0.48)",
      700: "rgba(255, 255, 255, 0.64)",
      800: "rgba(255, 255, 255, 0.80)",
      900: "rgba(255, 255, 255, 0.92)",
    },
    blackAlpha: {
      50: "rgba(40, 40, 40, 0.04)",
      100: "rgba(40, 40, 40, 0.06)",
      200: "rgba(40, 40, 40, 0.08)",
      300: "rgba(40, 40, 40, 0.16)",
      400: "rgba(40, 40, 40, 0.24)",
      500: "rgba(40, 40, 40, 0.36)",
      600: "rgba(40, 40, 40, 0.48)",
      700: "rgba(40, 40, 40, 0.64)",
      800: "rgba(40, 40, 40, 0.80)",
      900: "rgba(40, 40, 40, 0.92)",
    },
    gray: {
      50: "#FAFAFA",
      100: "#F3F3F7",
      200: "#ECEDEE",
      300: "#D5D2DA",
      400: "#A8A8B8",
      500: "#717196",
      600: "#4A4A68",
      700: "#2D2D48",
      800: "#1A1A2C",
      900: "#171723",
    },
    red: {
      50: "#F7EDED",
      100: "#FED7D7",
      200: "#FFC2C2",
      300: "#FFA3A3",
      400: "#E68F8F",
      500: "#EE6F6F",
      600: "#D35858",
      700: "#AF4949",
      800: "#943D3D",
      900: "#6A3131",
    },
    orange: {
      50: "#FFFAF0",
      100: "#F2DAD0",
      200: "#F1D5C9",
      300: "#EEC6A0",
      400: "#FFB978",
      500: "#DCA673",
      600: "#E19F61",
      700: "#E09751",
      800: "#DB8434",
      900: "#83523A",
    },
    yellow: {
      50: "#F8F6EB",
      100: "#F8F0C1",
      200: "#E0D9A1",
      300: "#E6DD9B",
      400: "#CFC36A",
      500: "#C8B080",
      600: "#C4BA72",
      700: "#B7AC5B",
      800: "#908744",
      900: "#7B722F",
    },
    hazel: {
      50: "#F8F8EB",
      100: "#E7E7D6",
      200: "#D1D19F",
      300: "#BABA81",
      400: "#B2B274",
      500: "#8D8D10",
      600: "#8D8D10",
      700: "#7F7F0E",
      800: "#72720D",
      900: "#545418",
    },
    green: {
      50: "#EBF8F0",
      100: "#D6E7DC",
      200: "#CEDDD4",
      300: "#81BAA5",
      400: "#74B2A6",
      500: "#108D72",
      600: "#108D74",
      700: "#0E7F68",
      800: "#0D725E",
      900: "#18544C",
    },
    teal: {
      50: "#E6FFFF",
      100: "#D5EDED",
      200: "#98CBCB",
      300: "#6DB8B8",
      400: "#57BDB9",
      500: "#319797",
      600: "#2C7A7B",
      700: "#285E61",
      800: "#234E52",
      900: "#1D4044",
    },
    blue: {
      50: "#E2F0FD",
      100: "#C6DFF8",
      200: "#B2D4E9",
      300: "#63B3ED",
      400: "#57A7E9",
      500: "#3182CE",
      600: "#2B6CB0",
      700: "#2C5282",
      800: "#2A4365",
      900: "#1A365D",
    },
    cyan: {
      50: "#E5F9F9",
      100: "#D3F5FC",
      200: "#C5F1F9",
      300: "#97E1EE",
      400: "#53C1D8",
      500: "#70B1C1",
      600: "#5895A3",
      700: "#418798",
      800: "#275C69",
      900: "#0F4A58",
    },
    purple: {
      50: "#F2EDF7",
      100: "#F2F0FE",
      200: "#DDCBF1",
      300: "#D5BBF3",
      400: "#B296D3",
      500: "#9377BF",
      600: "#856AB7",
      700: "#7157AB",
      800: "#5B4592",
      900: "#473676",
    },
    pink: {
      50: "#FFF5F7",
      100: "#FFD1DE",
      200: "#EBCCD5",
      300: "#F687B3",
      400: "#F388BC",
      500: "#FF4D82",
      600: "#BD508E",
      700: "#AB578C",
      800: "#92457B",
      900: "#763663",
    },
    // generated using https://uicolors.app/create from #282828
    brand: {
      50: "#f6f6f6",
      100: "#e7e7e7",
      200: "#d1d1d1",
      300: "#b0b0b0",
      400: "#888888",
      500: "#6d6d6d",
      600: "#5d5d5d",
      700: "#4f4f4f",
      800: "#454545",
      900: "#282828",
    },
    refine: {
      header: {
        bg: {
          light: "#FFFFFF",
          dark: "#CCCCCC",
        },
      },
      sider: {
        title: {
          light: "#FFFFFF",
          dark: "#FFFFFF",
        },
        header: {
          light: "#282828",
          dark: "#282828",
        },
        bg: {
          light: "#282828",
          dark: "#282828",
        },
      },
    },
  },
});
